/**
 * The persistent app-level navigation rail.
 *
 * Replaces the flat text nav `App.tsx` shipped since Task 13 with the shape a tool used all day
 * actually wants: one rail, icon and label, an obvious current-page state, and the two things that
 * belong beside navigation rather than above content — the theme toggle and the signed-in identity —
 * pinned to its foot instead of floating in a header that scrolled away with everything else.
 *
 * ## Why collapse is a local, unpersisted `useState`
 *
 * Unlike the theme, collapse is not a preference worth remembering across a session: it is "I need
 * the width back for this table" in the moment, and a value that appeared to persist across a reload
 * would surprise the next person who opens this on a shared terminal. `applyTheme` persists on
 * purpose; this deliberately does not.
 *
 * ## Why the labels never fully disappear
 *
 * Collapsed, the label becomes the icon's accessible name via `sr-only` rather than being removed:
 * every `NavLink` keeps a real accessible name whether or not the rail is showing it, so a screen
 * reader user's experience of the navigation does not depend on a sighted user's width preference.
 *
 * ## Why this is not `<aside>` nested inside `<nav>`
 *
 * The whole rail is `<nav aria-label="Navegación principal">`; the footer identity block sits outside
 * it as an ordinary `<div>`, because a role/session summary is not a navigation link and putting it
 * inside the nav landmark would tell a screen reader user it is one.
 */
import type { StaffRole } from '@apuestapr/contracts';
import { cn, focusRing, icons, type IconComponent } from '@apuestapr/ui';
import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

import { locationsRoutePath } from '../locations/paths.ts';
import { supportRoutePath } from '../support/paths.ts';
import { yubikeysRoutePath } from '../yubikeys/paths.ts';
import { staffRoleLabels } from './roleLabels.ts';
import { staffRoutePath } from '../staff/paths.ts';

export interface SidebarItem {
  readonly to: string;
  readonly label: string;
  readonly icon: IconComponent;
  readonly end?: boolean;
}

const contentItem: SidebarItem = { to: '/content/ofertas', label: 'Contenido', icon: icons.layers };
const supportItem: SidebarItem = { to: supportRoutePath, label: 'Soporte', icon: icons.inbox };
const locationsItem: SidebarItem = {
  to: locationsRoutePath,
  label: 'Localidades',
  icon: icons.building,
};
const yubikeysItem: SidebarItem = {
  to: yubikeysRoutePath,
  label: 'Llaves YubiKey',
  icon: icons.key,
};
const balancesItem: SidebarItem = {
  to: '/balances',
  label: 'Balances',
  icon: icons.refresh,
};
const documentsItem: SidebarItem = {
  to: '/documents',
  label: 'Documentos',
  icon: icons.fileText,
};
const analyticsItem: SidebarItem = {
  to: '/analytics',
  label: 'Analytics',
  icon: icons.layers,
};
const vipTiersItem: SidebarItem = {
  to: '/vip-tiers',
  label: 'VIP Tiers',
  icon: icons.users,
};
const demographicsItem: SidebarItem = {
  to: '/demographics',
  label: 'Demografía',
  icon: icons.users,
};
const staffItem: SidebarItem = {
  to: staffRoutePath,
  label: 'Personal',
  icon: icons.shield,
};
const ownerReportItem: SidebarItem = {
  to: '/owner-report',
  label: 'Resumen del día',
  icon: icons.home,
};
const homeItem: SidebarItem = { to: '/', label: 'Inicio', icon: icons.home, end: true };

export interface SidebarProps {
  readonly showContent: boolean;
  readonly showSupport: boolean;
  /** Locations and YubiKeys share one flag: both are `admin`/`manager`-only surfaces gated the
   * same way, since the location detail this list leads to and the pool both carry data an
   * `agente` may not see. */
  readonly showLocations: boolean;
  /** Staff management is admin only, so this is `true` for exactly one role. */
  readonly showStaff: boolean;
  /** See the identical note on `AppShellProps` — kept named the same across the two for consistency. */
  readonly staffRole: StaffRole;
  readonly userName: string;
  readonly footer: ReactElement;
  readonly collapsed: boolean;
  readonly onToggleCollapsed: () => void;
}

const NavItem = ({
  item,
  collapsed,
}: {
  readonly item: SidebarItem;
  readonly collapsed: boolean;
}): ReactElement => {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        end={item.end ?? false}
        to={item.to}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 font-medium transition-colors duration-fast ease-standard',
            focusRing,
            isActive
              ? 'bg-secondary text-secondary-foreground'
              : 'text-foreground hover:bg-muted hover:text-muted-foreground',
          )
        }
      >
        <Icon aria-hidden="true" />
        <span className={collapsed ? 'sr-only' : undefined}>{item.label}</span>
      </NavLink>
    </li>
  );
};

export const Sidebar = ({
  showContent,
  showSupport,
  showLocations,
  showStaff,
  staffRole,
  userName,
  footer,
  collapsed,
  onToggleCollapsed,
}: SidebarProps): ReactElement => {
  const items = [
    homeItem,
    ...(showContent ? [contentItem] : []),
    ...(showSupport ? [supportItem] : []),
    ...(showLocations ? [locationsItem, yubikeysItem, balancesItem] : []),
    ...(showContent ? [documentsItem, analyticsItem, demographicsItem, vipTiersItem] : []),
    ...(showStaff ? [ownerReportItem, staffItem] : []),
  ];
  const CollapseIcon = collapsed ? icons.sidebarExpand : icons.sidebarCollapse;

  return (
    <aside
      aria-label="Panel de navegación"
      className="flex h-full flex-col gap-4 border-r border-border bg-card p-4 text-card-foreground"
    >
      <div className="flex items-center justify-between gap-2">
        {/* The app's one h1. Every screen's own heading is an h2 under it, the same outline the
            replaced header/main split had — chrome names the app, content names the page. */}
        <h1
          className={cn(
            'm-0 font-display text-sm font-semibold',
            collapsed ? 'sr-only' : undefined,
          )}
        >
          ApuestaPR · Panel
        </h1>
        <button
          type="button"
          aria-pressed={collapsed}
          aria-label={collapsed ? 'Expandir la navegación' : 'Contraer la navegación'}
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground',
            'transition-colors duration-fast ease-standard hover:bg-muted hover:text-foreground',
            focusRing,
          )}
          onClick={onToggleCollapsed}
        >
          <CollapseIcon aria-hidden="true" />
        </button>
      </div>

      <nav aria-label="Navegación principal" className="flex-1">
        <ul className="m-0 grid list-none gap-1 p-0">
          {items.map((item) => (
            <NavItem key={item.to} item={item} collapsed={collapsed} />
          ))}
        </ul>
      </nav>

      <div className="flex flex-col gap-3 border-t border-border pt-4">
        <div className={cn('flex items-center gap-2', collapsed ? 'sr-only' : undefined)}>
          <icons.shield aria-hidden="true" className="text-muted-foreground" />
          <div className="min-w-0">
            <p className="m-0 truncate text-sm font-medium">{userName}</p>
            <p className="m-0 text-xs text-muted-foreground">{staffRoleLabels[staffRole]}</p>
          </div>
        </div>
        {footer}
      </div>
    </aside>
  );
};
